import { FC, useEffect, useState } from 'react'
import { Select } from 'antd'
import cn from 'classnames'
import Modal from '@common/Modal'
import Button from '@UI/New/Button'
import { usersAPI } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { IcoRemove } from '@/utils/icons'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import { IIndividualStudentsItem } from '../../types'
import { updateIndividualStudentTeacher } from '../IndividualStudentsTable/api'
import classes from './index.module.css'

interface EditTeacherModalProps {
  item?: IIndividualStudentsItem
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const EditTeacherModalContent: FC<EditTeacherModalProps> = ({
  item,
  open,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const [teacherId, setTeacherId] = useState<number | null>(null)
  const [teachers, setTeachers] = useState<ISelect[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const courseId = item?.course?.id
  const currentTeacherId = item?.teacher?.id

  useEffect(() => {
    if (!open || !courseId) return

    setTeacherId(currentTeacherId ?? null)
    setLoading(true)
    // Все педагоги платформы, а не только привязанные к курсу: бэкенд сам
    // привяжет курс выбранному сотруднику. directionId поднимает педагогов
    // этого курса в начало списка.
    usersAPI
      .getColleaguesForGroup({
        name: '',
        directionId: courseId,
        student_id: item?.student?.id,
      })
      .then((res) => {
        setTeachers(
          (res?.data?.data ?? []).map((colleague: any) => ({
            label: colleague?.name,
            value: Number(colleague?.id),
          })),
        )
      })
      .catch(() => {
        setTeachers([])
        addAlert({
          title: 'Ошибка',
          text: 'Не удалось загрузить педагогов по предмету',
          type: 'error',
        })
      })
      .finally(() => setLoading(false))
    // useAddAlert возвращает новую функцию на каждом рендере.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, currentTeacherId, open])

  const handleConfirm = async () => {
    if (!item || teacherId === null) return

    setSubmitting(true)
    const result = await updateIndividualStudentTeacher(item, teacherId)
    setSubmitting(false)

    if (!result.success) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось назначить педагога',
        type: 'error',
      })
      return
    }

    addAlert({
      title: 'Успешно',
      text: 'Педагог успешно назначен',
      type: 'success',
    })
    onSuccess()
    onClose()
  }

  return (
    <div className={mclasses.modalContainer}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>Выбрать педагога</span>
        </div>
        <div className={mclasses.closeIcon} onClick={onClose}>
          <IcoRemove />
        </div>
      </div>
      <div className={mclasses.formWrapper}>
        <div>
          <p className={mclasses.label}>Педагог</p>
          <Select
            showSearch
            className={cn(iclasses.newSelect)}
            popupClassName={cn(iclasses.newSelectPopup)}
            placeholder="Выберите педагога"
            value={teacherId}
            onChange={setTeacherId}
            options={teachers}
            loading={loading}
            notFoundContent="В данной франшизе нет педагогов"
            filterOption={(input, option) =>
              String(option?.label ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </div>
      </div>
      <div className={mclasses.actions}>
        <Button color="gray" onClick={onClose}>
          Отменить
        </Button>
        <Button
          color="primary"
          onClick={handleConfirm}
          disabled={teacherId === null || loading || submitting}
          isLoading={submitting}
        >
          Подтвердить
        </Button>
      </div>
    </div>
  )
}

const EditTeacherModal: FC<EditTeacherModalProps> = (props) => (
  <Modal
    active={props.open}
    className={cn(mclasses.modalWrapper, classes.modalWrapper) as any}
    setActive={props.onClose}
  >
    <EditTeacherModalContent {...props} />
  </Modal>
)

export default EditTeacherModal
